import React, { useState } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { api } from "../lib/api";
import { Sparkles, RefreshCw, AlertTriangle, Eye, Image as ImageIcon, FileScan } from "lucide-react";
import { toast } from "sonner";

/**
 * Doctor-side Phase 6 AI case summary card.
 *
 * Lazy-load: doesn't auto-fire to save LLM budget. The doctor taps
 * "Generate" → backend returns structured red-flag JSON → we cache on the
 * consultation. Subsequent reloads of the page show the cached summary
 * instantly; "Refresh" re-runs it with `force=true`.
 */
export default function CaseAiSummary({ consultationId, initial }) {
  const [data, setData] = useState(initial || null);
  const [busy, setBusy] = useState(false);

  const run = async (force = false) => {
    setBusy(true);
    try {
      const r = await api.post("/ai/case-summary", { consultation_id: consultationId, force });
      if (r.data?.ok === false) {
        toast.error(r.data.error || "Could not generate summary");
      } else {
        setData(r.data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI summary failed");
    } finally {
      setBusy(false);
    }
  };

  const urgencyColour = {
    high:     "bg-rose-100 text-rose-700 border-rose-200",
    moderate: "bg-amber-100 text-amber-700 border-amber-200",
    low:      "bg-emerald-100 text-emerald-700 border-emerald-200",
  }[(data?.urgency || "").toLowerCase()] || "bg-slate-100 text-slate-700 border-slate-200";

  return (
    <Card className="p-6 border-violet-200 bg-gradient-to-br from-violet-50/60 to-blue-50/40" data-testid="ai-case-summary">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-violet-700"/> AI case summary
            <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-white text-violet-700 border border-violet-200">Sonnet 4.5</span>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Aggregates the symptoms, demographics, and every AI-extracted file finding into one structured triage card.
            Decision-support only — never a diagnosis.
          </p>
        </div>
        {data ? (
          <Button variant="outline" size="sm" onClick={() => run(true)} disabled={busy} data-testid="ai-summary-refresh">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${busy ? "animate-spin" : ""}`}/> Refresh
          </Button>
        ) : (
          <Button size="sm" onClick={() => run(false)} disabled={busy} className="bg-violet-700 hover:bg-violet-800" data-testid="ai-summary-generate">
            <Sparkles className="w-3.5 h-3.5 mr-1"/> {busy ? "Generating…" : "Generate"}
          </Button>
        )}
      </div>

      {!data && !busy && (
        <p className="mt-4 text-xs text-slate-500 italic">No summary yet. Click <strong>Generate</strong> to run an LLM pass over the case.</p>
      )}

      {data && (
        <div className="mt-4 space-y-3">
          {data.urgency && (
            <div className="flex items-center gap-2 text-[11px] font-semibold">
              <span className={`px-2 py-0.5 rounded-full border uppercase tracking-wider ${urgencyColour}`}>
                Urgency: {data.urgency}
              </span>
              {data.cached && <span className="text-slate-400">(cached)</span>}
            </div>
          )}
          {data.summary && (
            <div className="rounded-lg border border-violet-200 bg-white p-3">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-violet-700">Summary</div>
              <div className="mt-1 text-sm text-slate-800 leading-relaxed">{data.summary}</div>
            </div>
          )}
          {(data.red_flags || []).length > 0 && (
            <div className="rounded-lg border border-rose-200 bg-rose-50/40 p-3" data-testid="ai-summary-red-flags">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-rose-700 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3"/> Red flags
              </div>
              <ul className="mt-1 list-disc pl-5 text-sm text-slate-800 space-y-0.5">
                {data.red_flags.map((f, i) => <li key={i}>{f}</li>)}
              </ul>
            </div>
          )}
          {(data.differential || []).length > 0 && (
            <div className="rounded-lg border border-blue-200 bg-white p-3">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-blue-700">Differential diagnoses</div>
              <ul className="mt-1 list-disc pl-5 text-sm text-slate-800 space-y-0.5">
                {data.differential.map((d, i) => <li key={i}>{d}</li>)}
              </ul>
            </div>
          )}
          {data.suggested_focus && (
            <div className="rounded-lg border border-violet-200 bg-white p-3">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-violet-700 flex items-center gap-1">
                <Eye className="w-3 h-3"/> Where to look hardest
              </div>
              <div className="mt-1 text-sm text-slate-800">{data.suggested_focus}</div>
            </div>
          )}
          {(data.tumour_indicators || []).length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3" data-testid="ai-summary-tumour">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-700 flex items-center gap-1">
                <ImageIcon className="w-3 h-3"/> Tumour / mass indicators
              </div>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {data.tumour_indicators.map((t, i) => (
                  <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-white text-amber-800 border border-amber-200">{t}</span>
                ))}
              </div>
            </div>
          )}
          {(data.suggested_imaging || []).length > 0 && (
            <div className="rounded-lg border border-blue-200 bg-white p-3">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-blue-700 flex items-center gap-1">
                <FileScan className="w-3 h-3"/> Recommend if not present
              </div>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {data.suggested_imaging.map((s, i) => (
                  <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">{s}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
